import { AfterViewInit, Component, OnInit, ViewChild, HostListener } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import WIDGED_SEND_MESSAGE_TYPES from '../../../../old-crazy-web-frontend/src/app/utils/widget.send.message.types';
import { getAuthTokenSelector } from '../auth/auth.reducer';
import { IState } from '../models/state';
import { environment } from '../../../environments/environment';
import { WebWidgetService } from './web-widget.service';
import { AccountService } from '../services/account.service';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { getUserDetailsSelector } from '../user/user.reducer';
import { getUsersSelector } from '../../users/users.reducer';
import {
  GetCurrentUserAction,
  GetUsersAction,
  SetBlockIncomingCalls
} from '../../users/users.action';
import {NgxPermissionsService} from "ngx-permissions";

@Component({
  selector: 'cc-web-widget',
  templateUrl: './web-widget.component.html',
  styleUrls: ['./web-widget.component.scss']
})
export class WebWidgetComponent implements AfterViewInit, OnInit {
  @ViewChild('iframe') iframe;
  public isOpened = false;
  public isBlockIncomingCallsState = false;
  public webWidgetUrl: SafeResourceUrl;
  public didUserInteractedWithPage = false;
  public userDetails$;
  public users$;
  private clickAnywhere$ = new Subject();
  private currentUserId: number;

  constructor(
    private store: Store<IState>,
    private sanitizer: DomSanitizer,
    private webWidgetService: WebWidgetService,
    private accountService: AccountService,
    private ngxPermissionsService: NgxPermissionsService
  ) {
    this.webWidgetUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.WEB_WIDGET_URL);
  }

  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent) {
    this.clickAnywhere$.next(true);
    this.didUserInteractedWithPage = true;
  }

  ngOnInit() {
    this.ngxPermissionsService.hasPermission('EDIT_USER_PERMISSION').then((result) => {
      if(result) {
        this.store.dispatch(new GetUsersAction());
      } else {
        this.store.dispatch(new GetCurrentUserAction());
      }
    });
    this.userDetails$ = this.store.select(getUserDetailsSelector);
    this.users$ = this.store.select(getUsersSelector);
    combineLatest([this.userDetails$, this.users$]).subscribe(([user, users]) => {
      this.currentUserId = user.id;
      if(users.length) {
        this.isBlockIncomingCallsState = users.find(userDetails => userDetails.id === user.id).doNotAcceptIncomingCalls;
      }
    })
  }

  ngAfterViewInit() {
    if (!this.iframe) {
      return;
    }

    this.webWidgetService.iframe = this.iframe.nativeElement;

    this.webWidgetService.listenForEventsInIframe().subscribe((event: MessageEvent) => {
      if (event.data && event.data.type === 'SET_BLOCK_INCOMING_CALLS') {
        this.isBlockIncomingCallsState = event.data.payload.blocked;
        // this.store.dispatch( new SetBlockIncomingCalls({blocked: event.data.payload.blocked, id: this.currentUserId}))
      } else if (event.data && event.data.type === 'INCOMING_CALL') {
        this.isOpened = true;
        this.handleIncomingCallNotification(event.data.payload.msisdn);
      } else if (event.data && event.data.type === 'GET_URL') {
        this.webWidgetService.sendEventIntoIframe(
          Object.assign(WIDGED_SEND_MESSAGE_TYPES.SEND_URL, { url: window.location.href })
        );
      }
    });

    this.webWidgetService.openIframe$.subscribe(() => {
      this.isOpened = true;
    });
  }

  handleIframeLoaded() {
    this.accountService.getAccountConfig().subscribe(conf => {
      let isFullyConfigured = conf.data;

      if (isFullyConfigured) {
        this.getDataAndSendToken();
      } else {
        this.accountService.configChanged.subscribe((isConfigured: boolean) => {
          isFullyConfigured = isConfigured;

          if (isFullyConfigured) {
            this.getDataAndSendToken();
          }
        });
      }
    });
  }

  getDataAndSendToken() {
    this.store
      .pipe(select(getAuthTokenSelector), withLatestFrom(this.store.pipe(select(getUserDetailsSelector))))
      .subscribe(([token, user]) => {
        if (this.didUserInteractedWithPage) {
          this.webWidgetService.sendEventIntoIframe(
            Object.assign(WIDGED_SEND_MESSAGE_TYPES.SEND_TOKEN, { token, domain: user.domain })
          );
        } else {
          this.clickAnywhere$.pipe(distinctUntilChanged()).subscribe(() => {
            this.webWidgetService.sendEventIntoIframe(
              Object.assign(WIDGED_SEND_MESSAGE_TYPES.SEND_TOKEN, { token, domain: user.domain })
            );
          });
        }
      });
  }

  handleIncomingCallNotification(msisdn) {
    if (!('Notification' in window)) {
      console.log('Notifications not supported in the browser');
    } else if (Notification['permission'] === 'granted') {
      this.createBrowserNotification('Incoming call', msisdn);
    } else if (Notification['permission'] !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.createBrowserNotification('Incoming call', msisdn);
        }
      });
    }
  }

  openWidget() {
    this.isOpened = !this.isOpened;
    this.requestNotifications();
  }

  requestNotifications() {
    if (!('Notification' in window)) {
      console.log('Notifications not supported in the browser');
    } else if (Notification['permission'] === 'granted') {
      return;
    } else if (Notification['permission'] !== 'denied') {
      Notification.requestPermission().then(permission => {
        console.log(`Notifications state: ${permission}`);
      });
    }
  }

  private createBrowserNotification(title, body) {
    const notification = new Notification(title, {
      body
    });

    notification.onclick = () => {
      window.focus();
    };
  }

  handleClickOutside(event) {
    this.isOpened = false;
  }
}
